/**
 * Log-safe parts of a Convex function failure.
 *
 * `ConvexHttpClient` rethrows the deployment's `errorMessage` verbatim:
 * `[Request ID: <id>] Server Error` then, on dev deployments, the uncaught
 * error and its frames, or an `ArgumentValidationError` with the validator
 * path and a dump of the offending value. The generic sanitizer cuts at the
 * first bracket, which loses everything. This keeps the request id, the error
 * class, the first line of its message (sanitized by the caller), the
 * validator path and the function name, and never the `Value:`/`Object:` dumps.
 */
import { logSafeLabel } from "./redact.ts";

export type ConvexServerErrorParts = {
  requestId: string;
  /** e.g. "Uncaught Error", "ArgumentValidationError"; absent on prod deployments. */
  kind?: string;
  /** First line of the error text, unsanitized. */
  detail?: string;
  /** Validator path such as `.patch.scoreList[0].id`. */
  path?: string;
};

const SERVER_ERROR = /^\[Request ID: ([A-Za-z0-9-]{1,64})\] Server Error\s*/;
const KIND = /^((?:Uncaught )?(?:[A-Za-z][A-Za-z0-9]{0,63})?(?:Error|Exception)):\s*/;
const VALIDATOR_PATH = /^(?:\.[A-Za-z_$][\w$]{0,63}|\[\d{1,6}\]){1,24}$/;
/** Backticked identifiers in validator messages are field names from the schema or the payload keys. */
const BACKTICKED = /`([^`\n]{0,80})`/g;

export function parseConvexServerError(message: string): ConvexServerErrorParts | null {
  const head = SERVER_ERROR.exec(message);
  if (!head) return null;
  const lines = message.slice(head[0].length).split("\n").map((line) => line.trim()).filter(Boolean);
  const parts: ConvexServerErrorParts = { requestId: head[1] };
  const first = lines.find((line) => !line.startsWith("at ") && !/^(Path|Value|Object|Validator):/.test(line));
  if (first) {
    const kind = KIND.exec(first);
    if (kind) parts.kind = kind[1];
    const detail = (kind ? first.slice(kind[0].length) : first)
      .replace(BACKTICKED, (_match, name: string) => logSafeLabel(name));
    if (detail) parts.detail = detail;
  }
  const path = lines.find((line) => line.startsWith("Path:"))?.slice("Path:".length).trim();
  if (path && VALIDATOR_PATH.test(path)) parts.path = path;
  return parts;
}

const CONVEX_FUNCTION = Symbol.for("smartmanager.convexFunction");
const FUNCTION_NAME = /^[A-Za-z0-9_][A-Za-z0-9_./-]{0,127}(?::[A-Za-z0-9_]{1,64})?$/;

/** The Convex function a failed client call named, when the client was wrapped. */
export function convexFunctionOf(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const name = (error as { [CONVEX_FUNCTION]?: unknown })[CONVEX_FUNCTION];
  return typeof name === "string" ? name : undefined;
}

type ConvexCallClient = {
  query: (...args: never[]) => Promise<unknown>;
  mutation: (...args: never[]) => Promise<unknown>;
  action: (...args: never[]) => Promise<unknown>;
};

/**
 * Tags errors thrown by `query`/`mutation`/`action` with the function name
 * (a symbol property, so it never serializes). The same error object is
 * rethrown, so `instanceof` and message checks are unaffected.
 */
export function tagConvexFunctionErrors<T extends ConvexCallClient>(client: T): T {
  const target = client as unknown as Record<"query" | "mutation" | "action", (name: unknown, ...rest: unknown[]) => Promise<unknown>>;
  for (const method of ["query", "mutation", "action"] as const) {
    const call = target[method].bind(client);
    target[method] = async (name: unknown, ...rest: unknown[]) => {
      try {
        return await call(name, ...rest);
      } catch (error) {
        if (typeof name === "string" && error && typeof error === "object" && !Object.isFrozen(error) && convexFunctionOf(error) === undefined) {
          Object.defineProperty(error, CONVEX_FUNCTION, { value: FUNCTION_NAME.test(name) ? name : "redacted", enumerable: false });
        }
        throw error;
      }
    };
  }
  return client;
}
